<template>
    <div :class="`dropdown-align-${align} dropdown`">
        <label for="dropdown" class="field-label">
            {{ fieldLabel }}
            <span v-if="isRequired" class="required-indicator">*</span>
        </label>
        <div class="input-container">
            <div :class="['select-wrapper', { 'error': showError && isRequired && !selectedValue, 'filled': selectedValue }]">
            <div class="custom-select">
                    <select 
                        id="dropdown" 
                        name="dropdown" 
                        v-model="selectedValue" 
                        @change="emitSelection"
                        @focus="onFocus"
                        @blur="onBlur"
                        :class="{ 'has-value': selectedValue }"
                    >
                        <option value="">{{ placeholder || "Seçiniz..." }}</option>
                    <option v-for="option in items" :key="option" :value="option">{{ option }}</option>
                </select>
                    <span class="arrow">
                        <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                            <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </span>
                </div>
                <div class="input-border"></div>
            </div>
            <label :class="['input-label', { 'error': showError && isRequired && !selectedValue }]" for="dropdown">{{ altLabel }}</label>
            <div v-if="showError && isRequired && !selectedValue" class="error-message">
                {{ fieldLabel }} alanı zorunludur
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "FormDropDownList",
    props: {
        fieldLabel: {
            type: String,
            required: true,
        },
        altLabel: {
            type: String,
            required: true,
        },
        items: {
            type: Array,
            required: true,
        },
        placeholder: {
            type: String,
            default: ''
        },
        align: {
            type: String,
            default: "top",
        },
        isRequired: {
            type: Boolean,
            default: false,
        },
        value: {
            type: [Object, String],
            default: () => ({ selected: "" }),
        },
        showError: {
            type: Boolean,
            default: false,
        },
        formId: {
            type: String,
            required: true,
        },
        order: {
            type: Number,
            required: true,
        }
    },
    data() {
        const safeValue = (typeof this.value === 'object' && this.value !== null) ? this.value : {};
        return {
            selectedValue: safeValue.selected || '',
        };
    },
    watch: {
        value: {
            handler(newValue) {
                const safeValue = (typeof newValue === 'object' && newValue !== null) ? newValue : {};
                this.selectedValue = safeValue.selected || '';
            },
            deep: true,
        },
        showError: {
            handler(newShowError) {
                if (newShowError) {
                    this.selectedValue = "";
                }
            },
        },
    },
    methods: {
        emitSelection() {
            this.$emit("input", {
                selected: this.selectedValue,
                fieldLabel: this.fieldLabel,
                altLabel: this.altLabel,
            });
            this.$emit("validate", {
                fieldId: this._uid,
                hasError: this.isRequired && (!this.selectedValue),
            });
            localStorage.setItem(`${this.formId}-${this.order}`, JSON.stringify({
                selected: this.selectedValue,
                fieldLabel: this.fieldLabel,
                altLabel: this.altLabel,
            }));
        },
        loadValues() {
            const savedData = localStorage.getItem(`${this.formId}-${this.order}`);
            if (savedData) {
                const parsedData = JSON.parse(savedData);
                this.selectedValue = parsedData.selected;
            }
        },
        onFocus(event) {
            event.target.parentElement.parentElement.classList.add('focused');
        },
        onBlur(event) {
            event.target.parentElement.parentElement.classList.remove('focused');
        }
    },
    mounted() {
        const hasSlugValue = this.value && typeof this.value === 'object' && this.value.selected;
        
        if (!hasSlugValue) {
            this.loadValues();
        }
        this.emitSelection();
    },
};
</script>
<style lang="scss" scoped>
/* Base DropDown Styles */
.dropdown {
    width: 100%;
    max-width: 700px;
    margin: clamp(0.5rem, 1.5vw, 1rem) auto;
    padding: clamp(0.625rem, 1.5vw, 0.875rem);
    position: relative;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    
    background: linear-gradient(145deg, 
        rgba(255, 255, 255, 0.02) 0%,
        rgba(255, 255, 255, 0.01) 100%);
    border-radius: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.06);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    
    &:hover {
        background: linear-gradient(145deg, 
            rgba(255, 255, 255, 0.03) 0%,
            rgba(255, 255, 255, 0.02) 100%);
    }

    .field-label {
        display: block;
        font-size: clamp(0.95rem, 2.2vw, 1.1rem);
        font-weight: 600;
        margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
        color: var(--text-primary, rgba(55, 65, 81, 0.9));
        line-height: 1.4;
        letter-spacing: -0.01em;
        
        .required-indicator {
            color: #ef4444;
            margin-left: 0.125rem;
            font-weight: 700;
        }
    }

    .input-container {
        position: relative;
        min-width: 0; // Flex item için minimum genişlik
    }

    .select-wrapper {
        position: relative;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

        .custom-select {
            position: relative;
            width: 100%;
            height: 2.75rem;
            display: flex;
            align-items: center;

            select {
                width: 100%;
                height: 100%;
                padding: 0 2.25rem 0 0.75rem;
                font-size: 0.9rem;
                line-height: 1.2;
                color: var(--text-primary, rgba(55, 65, 81, 0.9));
                background: rgba(255, 255, 255, 0.8);
                border: 1px solid rgba(209, 213, 219, 0.6);
                border-radius: 0.375rem;
                box-sizing: border-box;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                font-family: inherit;
                backdrop-filter: blur(10px);
                appearance: none;
                cursor: pointer;
                
                &:focus {
                    outline: none;
                    border-color: var(--primary, #6366f1);
                    background: rgba(255, 255, 255, 0.95);
                    box-shadow: 
                        0 0 0 3px rgba(99, 102, 241, 0.1),
                        0 4px 12px rgba(0, 0, 0, 0.05);
                }
                
                &:hover:not(:focus) {
                    border-color: rgba(107, 114, 128, 0.4);
                    background: rgba(255, 255, 255, 0.85);
                }
                
                option {
                    background: white;
                    color: rgba(55, 65, 81, 0.9);
                    padding: 0.5rem;
                    
                    &:first-child {
                        color: rgba(107, 114, 128, 0.7);
                    }
                }
            }

            .arrow {
                position: absolute;
                right: 0.75rem;
                top: 0;
                bottom: 0;
                width: 0.75rem;
                display: flex;
                align-items: center;
                justify-content: center;
                pointer-events: none;
                color: rgba(107, 114, 128, 0.7);
                
                svg {
                    width: 0.75rem;
                    height: 0.5rem;
                    display: block;
                    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }
            }
        }

        .input-border {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 2px;
            border-radius: 1px;
            transform: scaleX(0);
            transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        &.focused .input-border,
        select:focus ~ .input-border {
            transform: scaleX(1);
        }

        &.focused .custom-select .arrow {
            color: var(--primary, #6366f1);

            svg {
                transform: rotate(180deg);
            }
        }

        &.error {
            .custom-select select {
                border-color: #ef4444;
                background: rgba(254, 242, 242, 0.8);
                
                &:focus {
                    border-color: #ef4444;
                    box-shadow: 
                        0 0 0 3px rgba(239, 68, 68, 0.1),
                        0 4px 12px rgba(239, 68, 68, 0.1);
                }
            }
            
            .input-border {
                background: #ef4444;
            }
            
            .arrow {
                color: #ef4444;
            }
        }
    }

    .input-label {
        display: block;
        font-size: clamp(0.75rem, 1.6vw, 0.8rem);
        color: rgba(107, 114, 128, 0.8);
        margin-top: clamp(0.25rem, 0.8vw, 0.375rem);
        font-weight: 500;
        transition: color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        
        &.error {
            color: #ef4444;
        }
    }

    .error-message {
        color: #ef4444;
        font-size: clamp(0.8rem, 1.8vw, 0.875rem);
        margin-top: clamp(0.375rem, 1vw, 0.5rem);
        font-weight: 500;
        display: flex;
        align-items: center;
        gap: 0.375rem;
        
        &::before {
            content: '⚠';
            font-size: 0.875em;
        }
    }
}

/* Left Alignment - Label solda, select sağda */
.dropdown-align-left {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);
    text-align: left;

    .field-label {
        min-width: clamp(100px, 20vw, 140px);
        flex-shrink: 0;
        margin-bottom: 0;
        padding-top: clamp(0.6rem, 1.5vw, 0.75rem);
        text-align: left;
    }

    .input-container {
        flex: 1;
    }
    
    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0;
        
        .field-label {
            min-width: auto;
            padding-top: 0;
            margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
        }
        
        .input-container {
            width: 100%;
        }
    }
}

/* Right Alignment - Label solda sağa yaslanmış, select sağda */
.dropdown-align-right {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);

    .field-label {
        min-width: clamp(100px, 20vw, 140px);
        flex-shrink: 0;
        margin-bottom: 0;
        padding-top: clamp(0.6rem, 1.5vw, 0.75rem);
        text-align: right;
    }

    .input-container {
        flex: 1;
    }
    
    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0;
        
        .field-label {
            min-width: auto;
            padding-top: 0;
            margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
            text-align: left;
        }
        
        .input-container {
            width: 100%;
        }
    }
}

/* Center/Top Alignment (Default) */
.dropdown-align-top {
    text-align: left;
    flex-direction: column;
}

// Mobil optimizasyonları
@media (max-width: 768px) {
    .dropdown {
        margin: clamp(0.25rem, 1vw, 0.5rem) auto;
        padding: clamp(0.5rem, 1.2vw, 0.75rem);
        
        .field-label {
            font-size: clamp(0.9rem, 2vw, 1rem);
            margin-bottom: clamp(0.375rem, 1vw, 0.5rem);
        }
        
        .select-wrapper {
            .custom-select {
                height: 2.5rem;
            }

            .custom-select select {
                padding: 0 2rem 0 0.625rem;
                font-size: 0.875rem;
                border-radius: 0.5rem;
            }
            
            .arrow {
                right: 0.625rem;
                
                svg {
                    width: 0.75rem;
                    height: 0.5rem;
                }
            }
        }
        
        .input-label {
            font-size: clamp(0.75rem, 1.8vw, 0.8rem);
            margin-top: clamp(0.25rem, 0.8vw, 0.375rem);
        }
        
        .error-message {
            font-size: clamp(0.8rem, 1.8vw, 0.85rem);
            margin-top: clamp(0.25rem, 0.8vw, 0.375rem);
        }
    }
}

@media (max-width: 480px) {
    .dropdown {
        margin: 0.25rem auto;
        padding: 0.5rem;
        
        .select-wrapper {
            .custom-select {
                height: 2.5rem;
            }

            .custom-select select {
                padding: 0 2rem 0 0.625rem;
                font-size: 0.875rem;
            }
            
            .arrow {
                right: 0.625rem;
                
                svg {
                    width: 0.75rem;
                    height: 0.5rem;
                }
            }
        }
        
        .input-label {
            font-size: 0.75rem;
            margin-top: 0.25rem;
        }
        
        .error-message {
            font-size: 0.8rem;
            margin-top: 0.25rem;
        }
    }
}

/* Dark mode devre dışı - Her zaman açık tema kullan */

/* Reduced Motion Support */
@media (prefers-reduced-motion: reduce) {
    .dropdown,
    .select-wrapper,
    .select-wrapper select,
    .input-label,
    .input-border,
    .arrow {
        transition: none;
    }
    
    .select-wrapper select:focus {
        transform: none;
    }
    
    .select-wrapper.focused .arrow svg {
        transform: none;
    }
}

/* High Performance Mode */
.select-wrapper {
    will-change: transform;
    
    select {
        will-change: transform, box-shadow;
    }
    
    .input-border {
        will-change: transform;
    }
    
    .arrow {
        will-change: transform, color;
    }
}

.input-label {
    will-change: color;
}
</style>